<template>
  <section class="mt-7" data-testid="cockpit-milestone-list">
    <h2 class="cockpit-section-title">
      {{ $t('UserProjects.cockpit.milestones.title') }}
    </h2>
    <div class="cockpit-panel">
      <div v-if="!items.length" class="text-sm text-[var(--color-disabled)] py-2">
        {{ $t('UserProjects.cockpit.milestones.empty') }}
      </div>
      <div v-else class="space-y-1">
        <div
          v-for="item in items"
          :key="item.id"
          class="py-2 px-2 rounded"
          :class="{ 'bg-[#FAFAFA]': item.isCurrent }"
        >
          <div class="flex items-center gap-3">
            <span
              class="text-[16px] shrink-0 w-4 text-center"
              :style="{ color: item.iconColor }"
            >
              {{ item.icon }}
            </span>
            <span
              class="text-[13px] flex-1 min-w-0 truncate text-[var(--color-dark)]"
              :class="item.isCurrent ? 'font-bold' : 'font-medium'"
            >
              {{ item.title }}
            </span>
            <span class="text-[12px] text-[var(--color-disabled)] shrink-0">
              {{ item.dateLabel }}
            </span>
            <span
              class="text-[12px] shrink-0"
              :style="{ color: item.deviationColor }"
            >
              {{ item.deviationLabel }}
            </span>
          </div>
          <div
            v-if="item.isActive && item.commitments"
            class="text-[11px] text-[var(--color-disabled)] ml-7 mt-0.5"
            data-testid="cockpit-milestone-commitments"
          >
            PPC:
            <span class="font-medium" :style="{ color: item.ppcColor }">
              {{ item.ppcLabel }}
            </span>
            {{ ' · ' }}{{ item.commitments.done }}/{{ item.commitments.total }}
            {{ $t('UserProjects.cockpit.milestones.doneLabel') }}
            {{ ' · ' }}{{ item.commitments.open }}
            {{ $t('UserProjects.cockpit.milestones.openLabel') }}
            <span
              v-if="item.commitments.overdue > 0"
              class="text-[#EF4444]"
            >
              {{ ' · ' }}{{ item.commitments.overdue }}
              {{ $t('UserProjects.cockpit.milestones.overdueLabel') }}
            </span>
          </div>
        </div>
      </div>
      <button
        type="button"
        class="cockpit-link-btn mt-3"
        data-testid="cockpit-all-milestones"
        @click="$emit('view-all')"
      >
        {{ $t('UserProjects.cockpit.milestones.viewAll') }}
      </button>
    </div>
  </section>
</template>

<script>
import { resolvePpcAmpelHex } from '@/utils/cockpitDisplay';

export default {
  name: 'CockpitMilestoneList',
  props: {
    milestones: { type: Array, default: () => [] },
  },
  emits: ['view-all'],
  computed: {
    items() {
      return this.milestones.map((milestone) => {
        const ampel = milestone.ampel || 'green';
        const isDone = milestone.status === 'done' || Boolean(milestone.actualDate);
        const isActive = Boolean(milestone.isActive);
        const icon = isDone ? '✓' : (isActive ? '◉' : '○');
        const iconColor = isDone ? '#22C55E' : (isActive ? '#EAB308' : '#ABABAB');
        const deviation = milestone.deviation || {};
        let deviationLabel = '—';
        let deviationColor = '#ABABAB';
        if (deviation.key === 'daysEarly') {
          deviationLabel = this.$t('UserProjects.cockpit.milestones.daysEarly', { count: deviation.count });
          deviationColor = '#22C55E';
        } else if (deviation.key === 'daysLate' || deviation.key === 'daysOverdue') {
          deviationLabel = this.$t('UserProjects.cockpit.milestones.daysLate', { count: deviation.count });
          deviationColor = '#EAB308';
        } else if (ampel === 'yellow' || milestone.status === 'at_risk') {
          deviationLabel = this.$t('UserProjects.cockpit.milestones.atRisk');
          deviationColor = '#EF4444';
        } else if (ampel === 'red') {
          deviationLabel = this.$t('UserProjects.cockpit.milestones.overdue');
          deviationColor = '#EF4444';
        } else if (deviation.key === 'onTime' || milestone.status === 'on_track') {
          deviationLabel = this.$t('UserProjects.cockpit.milestones.onTrack');
        }

        const dateValue = milestone.dueDate || milestone.targetDate || milestone.actualDate;
        const dateLabel = dateValue
          ? new Date(dateValue).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit' })
          : '—';

        const commitments = milestone.commitments || null;
        const ppc = commitments?.ppc;
        const ppcColor = resolvePpcAmpelHex(ppc) || '#ABABAB';
        const ppcLabel = typeof ppc === 'number' ? `${ppc}%` : '—';

        return {
          id: milestone.id,
          title: milestone.title || '—',
          icon,
          iconColor,
          dateLabel,
          deviationLabel,
          deviationColor,
          isCurrent: Boolean(milestone.isCurrent),
          isActive,
          commitments,
          ppcColor,
          ppcLabel,
        };
      });
    },
  },
};
</script>

<style scoped>
.cockpit-section-title {
  margin: 0 0 0.75rem;
  font-size: 13px;
  color: #ababab;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 500;
}

.cockpit-panel {
  background: #fff;
  border-radius: 12px;
  padding: 1.25rem;
  border: 1px solid #e5e5e5;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.cockpit-link-btn {
  border: none;
  background: transparent;
  color: #6b6b6b;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
}

.cockpit-link-btn:hover {
  text-decoration: underline;
}
</style>
